import React, { useState, useMemo } from "react";
import { FormattedDate } from "../utils/dateFormatter";

import { X, ArrowRight, Plus, ArrowUp, ArrowDown } from "lucide-react";
import { Button } from "./ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogClose,
} from "./ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
} from "./ui/dropdown-menu";
import filterIcon from "../assets/images/trading/filter.svg";
import sortIcon from "../assets/images/trading/sort.svg";

const StatusIcon = ({ color }) => (
  <svg
    width="10"
    height="10"
    viewBox="0 0 10 10"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    className="w-[10px] h-[10px]"
  >
    <g clipPath="url(#clip0_2352_21030)">
      <path
        fillRule="evenodd"
        clipRule="evenodd"
        d="M5.73034 0.302516C5.32699 -0.100838 4.67302 -0.100839 4.26966 0.302515L3.41081 1.16138H2.19412C1.62369 1.16138 1.16127 1.6238 1.16127 2.19423V3.41091L0.302516 4.26966C-0.100838 4.67301 -0.100839 5.32698 0.302515 5.73034L1.16127 6.58909V7.80464C1.16127 8.37507 1.62369 8.8375 2.19412 8.8375H3.40965L4.26966 9.6975C4.67301 10.1009 5.32698 10.1009 5.73034 9.6975L6.59035 8.8375H7.8045C8.37493 8.8375 8.83736 8.37507 8.83736 7.80464V6.59045L9.6975 5.73034C10.1009 5.32699 10.1009 4.67302 9.6975 4.26966L8.83736 3.40955V2.19423C8.83736 1.6238 8.37493 1.16138 7.8045 1.16138H6.5892L5.73034 0.302516Z"
        fill={color}
      />
    </g>
    <defs>
      <clipPath id="clip0_2352_21030">
        <rect width="10" height="10" fill="white" />
      </clipPath>
    </defs>
  </svg>
);

const TransactionHistoryAll = ({
  transactionsComplete,
  openAll,
  setOpenAll,
  onOpenSingle,
  setIsFromAllTr,
  walletAssetsData,
  filterType: parentFilterType,
  filterStatus: parentFilterStatus,
  sortBy: parentSortBy,
  setFilterType: setParentFilterType,
  setFilterStatus: setParentFilterStatus,
  setSortBy: setParentSortBy,
  formatter,
  formatTransactionDate,
  getTransactionIconComponent,
  getTransactionActionText,
  getTransactionActionTextMobile,
  getTransactionAmounts,
}) => {
  const [filterType, setFilterType] = useState(parentFilterType || "all");
  const [filterStatus, setFilterStatus] = useState(parentFilterStatus || "all");
  const [sortBy, setSortBy] = useState(parentSortBy || "date-desc");

  const assetImageMap = useMemo(() => {
    const map = new Map();
    if (walletAssetsData?.assets) {
      walletAssetsData.assets.forEach((asset) => {
        if (asset.asset && asset.image) {
          map.set(asset.asset.toUpperCase(), asset.image);
        }
      });
    }
    return map;
  }, [walletAssetsData]);

  const getAssetImageLocal = (assetSymbol) => {
    return assetImageMap.get(assetSymbol?.toUpperCase()) || "";
  };

  const getNumericAmountForSorting = (tx) => {
    const subtype = tx.transactionSubtype?.toLowerCase();
    const transactionType = tx.transactionType;

    if (transactionType === "UserTrade") {
      if (subtype === "buy") {
        return parseAmount(
          tx.transactionRef?.incomingAmount?.$numberDecimal ||
            tx.transactionRef?.incomingAmount ||
            tx.transactionRef?.outgoingAmount?.$numberDecimal ||
            tx.transactionRef?.outgoingAmount ||
            tx.transactionRef?.total?.$numberDecimal ||
            tx.transactionRef?.totalAmount?.$numberDecimal ||
            tx.amount ||
            0
        );
      } else if (subtype === "sell") {
        return parseAmount(
          tx.transactionRef?.outgoingAmount?.$numberDecimal ||
            tx.transactionRef?.outgoingAmount ||
            tx.transactionRef?.incomingAmount?.$numberDecimal ||
            tx.transactionRef?.incomingAmount ||
            tx.transactionRef?.total?.$numberDecimal ||
            tx.transactionRef?.totalAmount?.$numberDecimal ||
            tx.amount ||
            0
        );
      }
    }

    return parseAmount(
      tx.transactionRef?.amount?.$numberDecimal ||
        tx.transactionRef?.amount ||
        tx.transactionRef?.totalAmount?.$numberDecimal ||
        tx.transactionRef?.total?.$numberDecimal ||
        tx.amount ||
        0
    );
  };

  const filteredAndSortedTransactions = useMemo(() => {
    let transactions = transactionsComplete || [];

    if (filterType !== "all") {
      const filterTypeLower = filterType.toLowerCase();
      transactions = transactions.filter((tx) => {
        const txSubtype = tx.transactionSubtype?.toLowerCase();
        return txSubtype === filterTypeLower;
      });
    }

    if (filterStatus !== "all") {
      const filterStatusLower = filterStatus.toLowerCase();
      transactions = transactions.filter((tx) => {
        const txStatus = tx.status?.toLowerCase();
        if (filterStatusLower === "complete") {
          return txStatus === "complete" || txStatus === "completed";
        }
        return txStatus === filterStatusLower;
      });
    }

    transactions = [...transactions].sort((a, b) => {
      if (sortBy === "date-desc") {
        const dateA = a.createdAt ? new Date(a.createdAt).getTime() : 0;
        const dateB = b.createdAt ? new Date(b.createdAt).getTime() : 0;
        return dateB - dateA;
      } else if (sortBy === "date-asc") {
        const dateA = a.createdAt ? new Date(a.createdAt).getTime() : 0;
        const dateB = b.createdAt ? new Date(b.createdAt).getTime() : 0;
        return dateA - dateB;
      } else if (sortBy === "amount-desc") {
        const amountA = getNumericAmountForSorting(a);
        const amountB = getNumericAmountForSorting(b);
        return amountB - amountA;
      } else if (sortBy === "amount-asc") {
        const amountA = getNumericAmountForSorting(a);
        const amountB = getNumericAmountForSorting(b);
        return amountA - amountB;
      }
      return 0;
    });

    return transactions;
  }, [transactionsComplete, filterType, filterStatus, sortBy]);

  const handleSingleTransaction = (order) => {
    onOpenSingle?.(order);
    setIsFromAllTr(true);
  };

  const handleFilterChange = (value) => {
    setFilterType(value);
    if (setParentFilterType) setParentFilterType(value);
  };

  const handleStatusChange = (value) => {
    setFilterStatus(value);
    if (setParentFilterStatus) setParentFilterStatus(value);
  };

  const handleSortChange = (value) => {
    setSortBy(value);
    if (setParentSortBy) setParentSortBy(value);
  };

  const formatDate = (createdAt) => {
    if (formatTransactionDate) {
      return formatTransactionDate(createdAt);
    }
    if (!createdAt) return "";
    const now = new Date();
    const transactionDate = new Date(createdAt);
    const isToday = now.toDateString() === transactionDate.toDateString();
    if (isToday) {
      const hours = transactionDate.getHours();
      const minutes = transactionDate.getMinutes();
      const ampm = hours >= 12 ? "PM" : "AM";
      const displayHours = hours % 12 || 12;
      const displayMinutes = minutes.toString().padStart(2, "0");
      return `Today, ${displayHours}:${displayMinutes} ${ampm}`;
    }
    const dateFormat = new FormattedDate();
    return dateFormat.simpleFormat(createdAt);
  };

  const getIconComponent = (tx) => {
    if (getTransactionIconComponent) {
      return getTransactionIconComponent(tx);
    }
    const subtype = tx.transactionSubtype?.toLowerCase();
    if (subtype === "buy" || subtype === "deposit" || subtype === "incoming") {
      return Plus;
    } else if (
      subtype === "withdrawal" ||
      subtype === "outgoing" ||
      subtype === "send"
    ) {
      return ArrowUp;
    } else if (subtype === "receive") {
      return ArrowDown;
    }
    return Plus;
  };

  const getActionText = (tx) => {
    if (getTransactionActionText) {
      return getTransactionActionText(tx);
    }
    const subtype = tx.transactionSubtype?.toLowerCase();
    const transactionType = tx.transactionType;

    let asset = tx.asset?.toUpperCase() || tx.currency?.toUpperCase() || "";

    if (transactionType === "UserTrade" && subtype === "sell") {
      asset = tx.transactionRef?.outgoingAsset?.toUpperCase() || asset;
    } else if (transactionType === "UserTrade" && subtype === "buy") {
      asset = tx.transactionRef?.incomingAsset?.toUpperCase() || asset;
    }

    if (subtype === "buy") return `Bought ${asset}`;
    if (subtype === "sell") return `Sold ${asset}`;
    if (subtype === "deposit") return `Deposited ${asset}`;
    if (subtype === "withdrawal") return `Withdrew ${asset}`;
    if (subtype === "send" || subtype === "outgoing") return `Sent ${asset}`;
    if (subtype === "receive" || subtype === "incoming")
      return `Received ${asset}`;
    return `${subtype?.charAt(0).toUpperCase() + subtype?.slice(1)} ${asset}`;
  };

  const getActionTextMobile = (tx) => {
    if (getTransactionActionTextMobile) {
      return getTransactionActionTextMobile(tx);
    }
    const subtype = tx.transactionSubtype?.toLowerCase();
    if (subtype === "buy") return "Buy";
    if (subtype === "sell") return "Sell";
    if (subtype === "deposit") return "Deposited";
    if (subtype === "withdrawal") return "Withdrew";
    if (subtype === "send" || subtype === "outgoing") return "Sent";
    if (subtype === "receive" || subtype === "incoming") return "Received";
    return subtype?.charAt(0).toUpperCase() + subtype?.slice(1) || "";
  };

  const parseAmount = (value) => {
    if (value === null || value === undefined || value === "") return 0;

    if (value === 0) return 0;

    if (typeof value === "object" && value !== null) {
      if (value.$numberDecimal) {
        const parsed = parseFloat(value.$numberDecimal);
        return isNaN(parsed) ? 0 : parsed;
      }
      return 0;
    }

    if (typeof value === "string") {
      let cleaned = value
        .trim()
        .replace(/^[R$ZARUSD\s]+/i, "")
        .replace(/\s/g, "")
        .replace(/,/g, ".");

      const periodCount = (cleaned.match(/\./g) || []).length;
      if (periodCount > 1) {
        const lastPeriodIndex = cleaned.lastIndexOf(".");
        cleaned =
          cleaned.substring(0, lastPeriodIndex).replace(/\./g, "") +
          cleaned.substring(lastPeriodIndex);
      }

      const parsed = parseFloat(cleaned);
      return isNaN(parsed) ? 0 : parsed;
    }

    if (typeof value === "number") {
      return isNaN(value) ? 0 : value;
    }

    return 0;
  };

  const isFiatCurrency = (asset) => {
    if (!asset) return false;
    const assetUpper = asset.toUpperCase();
    const fiatCurrencies = [
      "ZAR",
      "USD",
      "EUR",
      "GBP",
      "JPY",
      "AUD",
      "CAD",
      "CHF",
      "CNY",
    ];
    return fiatCurrencies.includes(assetUpper);
  };

  const determineSellAssets = (tx) => {
    const asset = tx.asset?.toUpperCase();
    const currency = tx.currency?.toUpperCase();

    if (asset && currency) {
      const assetIsFiat = isFiatCurrency(asset);
      const currencyIsFiat = isFiatCurrency(currency);

      if (assetIsFiat && !currencyIsFiat) {
        return { outgoingAsset: currency, incomingAsset: asset };
      } else if (!assetIsFiat && currencyIsFiat) {
        return { outgoingAsset: asset, incomingAsset: currency };
      } else if (!assetIsFiat && !currencyIsFiat) {
        return { outgoingAsset: asset, incomingAsset: currency || "ZAR" };
      } else {
        return {
          outgoingAsset: asset || currency,
          incomingAsset: currency || asset || "ZAR",
        };
      }
    }

    return {
      outgoingAsset: asset || currency || "",
      incomingAsset: currency || asset || "ZAR",
    };
  };

  const getAmounts = (tx) => {
    if (getTransactionAmounts) {
      return getTransactionAmounts(tx);
    }

    const subtype = tx.transactionSubtype?.toLowerCase();
    const transactionType = tx.transactionType;
    let primaryAmount = "";
    let secondaryAmount = "";
    let totalCost = "";

    if (transactionType === "UserTrade") {
      let incomingAsset = tx.transactionRef?.incomingAsset?.toUpperCase();
      let outgoingAsset = tx.transactionRef?.outgoingAsset?.toUpperCase();

      if (!incomingAsset || !outgoingAsset) {
        if (subtype === "buy") {
          incomingAsset =
            incomingAsset ||
            tx.asset?.toUpperCase() ||
            tx.currency?.toUpperCase() ||
            null;
          outgoingAsset =
            outgoingAsset ||
            tx.currency?.toUpperCase() ||
            tx.asset?.toUpperCase() ||
            "ZAR";
        } else if (subtype === "sell") {
          const sellAssets = determineSellAssets(tx);
          incomingAsset = incomingAsset || sellAssets.incomingAsset;
          outgoingAsset = outgoingAsset || sellAssets.outgoingAsset;
        }
      }

      if (subtype === "buy") {
        const cryptoAmount = parseAmount(
          tx.transactionRef?.incomingAmount?.$numberDecimal ||
            tx.transactionRef?.incomingAmount ||
            tx.transactionRef?.total?.$numberDecimal ||
            tx.transactionRef?.totalAmount?.$numberDecimal
        );

        if (incomingAsset && (cryptoAmount > 0 || cryptoAmount === 0)) {
          primaryAmount =
            formatter?.format(cryptoAmount, incomingAsset) ||
            `${cryptoAmount} ${incomingAsset}`;
        }

        const price = parseAmount(
          tx.transactionRef?.price?.$numberDecimal ||
            tx.transactionRef?.price ||
            0
        );

        let fiatAmount = 0;
        if (price > 0 && cryptoAmount > 0) {
          fiatAmount = cryptoAmount * price;
        } else {
          fiatAmount = parseAmount(
            tx.transactionRef?.outgoingAmount?.$numberDecimal ||
              tx.transactionRef?.outgoingAmount ||
              tx.transactionRef?.total?.$numberDecimal ||
              tx.transactionRef?.totalAmount?.$numberDecimal ||
              tx.transactionRef?.amount?.$numberDecimal ||
              tx.transactionRef?.amount ||
              0
          );
        }

        if (outgoingAsset && (fiatAmount > 0 || fiatAmount === 0)) {
          secondaryAmount =
            formatter?.format(fiatAmount, outgoingAsset) ||
            `${fiatAmount} ${outgoingAsset}`;
        }

        const totalCostAmount = fiatAmount;

        if (outgoingAsset && (totalCostAmount > 0 || totalCostAmount === 0)) {
          totalCost =
            formatter?.format(totalCostAmount, outgoingAsset) ||
            `${totalCostAmount} ${outgoingAsset}`;
        }
      } else if (subtype === "sell") {
        const cryptoAmount = parseAmount(
          tx.transactionRef?.outgoingAmount?.$numberDecimal ||
            tx.transactionRef?.outgoingAmount ||
            tx.transactionRef?.total?.$numberDecimal ||
            tx.transactionRef?.totalAmount?.$numberDecimal
        );

        if (outgoingAsset && (cryptoAmount > 0 || cryptoAmount === 0)) {
          primaryAmount =
            formatter?.format(cryptoAmount, outgoingAsset) ||
            `${cryptoAmount} ${outgoingAsset}`;
        }

        const totalCostAmount = parseAmount(
          tx.transactionRef?.totalAmount?.$numberDecimal ||
            tx.transactionRef?.total?.$numberDecimal ||
            tx.transactionRef?.outgoingAmount?.$numberDecimal ||
            tx.transactionRef?.outgoingAmount ||
            cryptoAmount ||
            0
        );

        if (outgoingAsset && (totalCostAmount > 0 || totalCostAmount === 0)) {
          totalCost =
            formatter?.format(totalCostAmount, outgoingAsset) ||
            `${totalCostAmount} ${outgoingAsset}`;
        }

        const fiatAmount = parseAmount(
          tx.transactionRef?.incomingAmount?.$numberDecimal ||
            tx.transactionRef?.incomingAmount ||
            tx.transactionRef?.total?.$numberDecimal ||
            tx.transactionRef?.totalAmount?.$numberDecimal ||
            tx.transactionRef?.amount?.$numberDecimal
        );

        if (
          incomingAsset &&
          incomingAsset !== outgoingAsset &&
          (fiatAmount > 0 || fiatAmount === 0)
        ) {
          secondaryAmount =
            formatter?.format(fiatAmount, incomingAsset) ||
            `${fiatAmount} ${incomingAsset}`;
        }
      }
    } else {
      const asset = tx.asset?.toUpperCase() || tx.currency?.toUpperCase() || "";

      const amount = parseAmount(
        tx.transactionRef?.amount?.$numberDecimal ||
          tx.transactionRef?.amount ||
          tx.transactionRef?.totalAmount?.$numberDecimal ||
          tx.amount ||
          0
      );

      if (asset && (amount > 0 || amount === 0)) {
        primaryAmount =
          formatter?.format(amount, asset) || `${amount} ${asset}`;
      } else if (amount > 0 || amount === 0) {
        primaryAmount =
          formatter?.format(amount, "ZAR") || `R ${amount.toFixed(2)}`;
      }

      const isSendOrReceiveOrDepositOrWithdrawal =
        subtype === "send" ||
        subtype === "receive" ||
        subtype === "deposit" ||
        subtype === "withdrawal" ||
        subtype === "incoming" ||
        subtype === "outgoing";

      if (
        asset &&
        asset !== "ZAR" &&
        !isSendOrReceiveOrDepositOrWithdrawal &&
        (amount > 0 || amount === 0)
      ) {
        const zarValue = parseAmount(
          tx.valueInZar ||
            (tx.transactionRef?.price?.$numberDecimal &&
              amount * parseFloat(tx.transactionRef.price.$numberDecimal)) ||
            tx.transactionRef?.amount?.$numberDecimal ||
            0
        );

        if (zarValue > 0 || zarValue === 0) {
          secondaryAmount =
            formatter?.format(zarValue, "ZAR") || `R ${zarValue.toFixed(2)}`;
        }
      }

      if (
        transactionType === "Payment" ||
        transactionType === "CryptoTransaction" ||
        transactionType === "FiatTransaction"
      ) {
        const totalAmount = parseAmount(
          tx.transactionRef?.totalAmount?.$numberDecimal ||
            tx.transactionRef?.total?.$numberDecimal ||
            amount
        );
        if (asset && (totalAmount > 0 || totalAmount === 0)) {
          totalCost =
            formatter?.format(totalAmount, asset) || `${totalAmount} ${asset}`;
        }
      } else {
        totalCost = primaryAmount || "";
      }
    }

    return { primaryAmount, secondaryAmount, totalCost };
  };

  return (
    <Dialog open={openAll} onOpenChange={setOpenAll}>
      <DialogContent className="max-w-[1200px] w-[95vw] rounded-2xl p-0 flex-1 flex flex-col max-h-[90vh] bg-white">
        <DialogClose asChild>
          <Button
            variant="ghost"
            onClick={() => setOpenAll(false)}
            className="w-8 h-8 sm:w-10 sm:h-10 absolute top-3 right-3 sm:top-4 sm:right-4 text-gray-400 hover:text-gray-600 rounded-full p-0 flex items-center justify-center border border-gray-200 hover:bg-gray-100 z-10"
          >
            <X size={16} className="text-gray-700 sm:w-[18px] sm:h-[18px]" />
          </Button>
        </DialogClose>

        <DialogHeader className="px-6 pt-6 pb-4 border-b">
          <div className="flex justify-between items-center">
            <DialogTitle className="text-[#6D6584] text-[20px] font-semibold">
              Transaction History
            </DialogTitle>
            <div className="flex items-center justify-end gap-2">
              <div className="items-center gap-2 mr-10 hidden md:flex">
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button
                      variant="outline"
                      className={`h-[48px] flex items-center justify-center w-full lg:!w-[107px] bg-white border border-[#EBE9EF] text-[#6D6584] rounded-full text-[14px] font-semibold hover:bg-gray-50 transition-colors gap-2 ${
                        filterType !== "all" || filterStatus !== "all"
                          ? "border-[#EB3366] bg-[#EB3366]/5"
                          : ""
                      }`}
                    >
                      <span>Filter</span>
                      <img
                        src={filterIcon}
                        alt="filter"
                        className="w-[16px] h-[16px] text-[#6D6584]"
                      />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="w-56">
                    <DropdownMenuLabel>Filter by Type</DropdownMenuLabel>
                    <DropdownMenuRadioGroup
                      value={filterType}
                      onValueChange={handleFilterChange}
                    >
                      <DropdownMenuRadioItem value="all">
                        All Types
                      </DropdownMenuRadioItem>
                      <DropdownMenuRadioItem value="buy">
                        Buy
                      </DropdownMenuRadioItem>
                      <DropdownMenuRadioItem value="sell">
                        Sell
                      </DropdownMenuRadioItem>
                      <DropdownMenuRadioItem value="deposit">
                        Deposit
                      </DropdownMenuRadioItem>
                      <DropdownMenuRadioItem value="withdrawal">
                        Withdrawal
                      </DropdownMenuRadioItem>
                      <DropdownMenuRadioItem value="send">
                        Send
                      </DropdownMenuRadioItem>
                      <DropdownMenuRadioItem value="receive">
                        Receive
                      </DropdownMenuRadioItem>
                      <DropdownMenuRadioItem value="incoming">
                        Incoming
                      </DropdownMenuRadioItem>
                      <DropdownMenuRadioItem value="outgoing">
                        Outgoing
                      </DropdownMenuRadioItem>
                    </DropdownMenuRadioGroup>
                    <DropdownMenuSeparator />
                    <DropdownMenuLabel>Filter by Status</DropdownMenuLabel>
                    <DropdownMenuRadioGroup
                      value={filterStatus}
                      onValueChange={handleStatusChange}
                    >
                      <DropdownMenuRadioItem value="all">
                        All Statuses
                      </DropdownMenuRadioItem>
                      <DropdownMenuRadioItem value="complete">
                        Complete
                      </DropdownMenuRadioItem>
                      <DropdownMenuRadioItem value="pending">
                        Pending
                      </DropdownMenuRadioItem>
                      <DropdownMenuRadioItem value="awaiting settlement">
                        Awaiting Settlement
                      </DropdownMenuRadioItem>
                    </DropdownMenuRadioGroup>
                    {(filterType !== "all" || filterStatus !== "all") && (
                      <>
                        <DropdownMenuSeparator />
                        <DropdownMenuItem
                          onClick={() => {
                            handleFilterChange("all");
                            handleStatusChange("all");
                          }}
                          className="text-red-600 focus:text-red-600"
                        >
                          Clear Filters
                        </DropdownMenuItem>
                      </>
                    )}
                  </DropdownMenuContent>
                </DropdownMenu>
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button
                      variant="outline"
                      className={`h-[48px] flex items-center justify-center w-full lg:!w-[107px] bg-white border border-[#EBE9EF] text-[#6D6584] rounded-full text-[14px] font-semibold hover:bg-gray-50 transition-colors gap-2 ${
                        sortBy !== "date-desc"
                          ? "border-[#EB3366] bg-[#EB3366]/5"
                          : ""
                      }`}
                    >
                      <span>Sort</span>
                      <img
                        src={sortIcon}
                        alt="sort"
                        className="w-[16px] h-[16px] text-[#6D6584]"
                      />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="w-56">
                    <DropdownMenuLabel>Sort by</DropdownMenuLabel>
                    <DropdownMenuRadioGroup
                      value={sortBy}
                      onValueChange={handleSortChange}
                    >
                      <DropdownMenuRadioItem value="date-desc">
                        Date (Newest First)
                      </DropdownMenuRadioItem>
                      <DropdownMenuRadioItem value="date-asc">
                        Date (Oldest First)
                      </DropdownMenuRadioItem>
                      <DropdownMenuRadioItem value="amount-desc">
                        Amount (Highest First)
                      </DropdownMenuRadioItem>
                      <DropdownMenuRadioItem value="amount-asc">
                        Amount (Lowest First)
                      </DropdownMenuRadioItem>
                    </DropdownMenuRadioGroup>
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>
            </div>
          </div>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto px-6 py-4">
          {/* Column Headers */}
          <div className="px-0 pb-4 hidden md:grid grid-cols-12 gap-4">
            <div className="col-span-4 text-[#6D6584] font-semibold text-sm">
              Action
            </div>
            <div className="col-span-4 text-[#6D6584] font-semibold text-[14px]">
              Amount
            </div>
            <div className="col-span-3 text-[#6D6584] font-semibold text-[14px]">
              Status
            </div>
            <div className="col-span-1"></div>
          </div>

          <div className="md:space-y-7 space-y-3 pt-4">
            {filteredAndSortedTransactions?.length > 0 ? (
              filteredAndSortedTransactions.map((transaction) => {
                const transactionType = transaction.transactionType;
                const subtype = transaction.transactionSubtype?.toLowerCase();

                let assetSymbol =
                  transaction.asset?.toUpperCase() ||
                  transaction.currency?.toUpperCase() ||
                  "";

                if (transactionType === "UserTrade" && subtype === "sell") {
                  assetSymbol =
                    transaction.transactionRef?.outgoingAsset?.toUpperCase() ||
                    assetSymbol;
                } else if (
                  transactionType === "UserTrade" &&
                  subtype === "buy"
                ) {
                  assetSymbol =
                    transaction.transactionRef?.incomingAsset?.toUpperCase() ||
                    assetSymbol;
                }

                const assetImage = getAssetImageLocal(assetSymbol);
                const IconComponent = getIconComponent(transaction);
                const actionText = getActionText(transaction);
                const actionTextMobile = getActionTextMobile(transaction);
                const { primaryAmount, secondaryAmount, totalCost } =
                  getAmounts(transaction);
                const status = transaction.status;

                return (
                  <div
                    key={transaction?._id}
                    className="flex items-center justify-between gap-4 md:grid md:grid-cols-12 md:gap-4 md:border-none border border-[#F1EDF7] border-[1px] rounded-2xl p-4 md:p-0"
                  >
                    <div className="col-span-4 flex items-center gap-3 min-w-0">
                      <div className="relative flex-shrink-0">
                        <div className="w-[40px] h-[40px] rounded-full bg-[#EB3366] flex items-center justify-center">
                          <IconComponent className="w-[16px] h-[16px] text-white" />
                        </div>
                        {assetImage && (
                          <div className="absolute -bottom-1 -right-1 w-[23px] h-[23px] rounded-full bg-blue-500 flex items-center justify-center border-2 border-white overflow-hidden">
                            <img
                              src={assetImage}
                              alt={assetSymbol}
                              className="w-[23px] h-[23px] object-contain"
                            />
                          </div>
                        )}
                      </div>
                      <div className="min-w-0 flex-1 md:space-y-2 space-y-1">
                        <div className="font-semibold text-[#33125F] text-[16px] md:text-[20px]">
                          <span className="md:hidden">{actionTextMobile}</span>
                          <span className="hidden md:inline">{actionText}</span>
                        </div>
                        <div className="text-[#6D6584] text-xs md:text-[14px]">
                          {formatDate(transaction.createdAt)}
                        </div>
                      </div>
                    </div>

                    <div className="col-span-4 hidden md:flex flex-col space-y-2">
                      <div className="font-semibold text-[#33125F] text-[16px] md:text-[20px]">
                        {primaryAmount}
                      </div>
                      {secondaryAmount && (
                        <div className="text-[#6D6584] text-xs md:text-[14px]">
                          {secondaryAmount}
                        </div>
                      )}
                    </div>

                    <div className="md:hidden flex flex-col items-end">
                      <div className="font-semibold text-[#33125F] text-[16px] md:text-[20px]">
                        {primaryAmount}
                      </div>
                      {secondaryAmount && (
                        <div className="text-[#6D6584] text-xs md:text-[14px]">
                          {secondaryAmount}
                        </div>
                      )}
                      {status && (
                        <div className="flex items-center gap-2 text-[14px] font-bold mt-1">
                          {status === "Pending" ||
                          status === "Awaiting Settlement" ? (
                            <div className="text-[10px] font-bold text-[#6375E6]">
                              {status.toUpperCase()}
                            </div>
                          ) : status === "Complete" ||
                            status === "Completed" ? (
                            <div className="text-[10px] font-bold text-[#17C3A5]">
                              {status.toUpperCase()}
                            </div>
                          ) : (
                            <div className="text-[10px] font-bold text-[#FFC107]">
                              {status.toUpperCase()}
                            </div>
                          )}
                        </div>
                      )}
                    </div>

                    <div className="col-span-3 hidden md:block">
                      {status && (
                        <div className="flex items-center gap-2 text-[14px] font-bold">
                          {status === "Pending" ||
                          status === "Awaiting Settlement" ? (
                            <div className="flex items-center gap-2 bg-[#EFF1FC] px-3 py-1 rounded-full">
                              <StatusIcon color="#6375E6" />
                              <span className="text-[10px] font-bold text-[#6375E6]">
                                {status.toUpperCase()}
                              </span>
                            </div>
                          ) : status === "Complete" ||
                            status === "Completed" ? (
                            <div className="flex items-center gap-2 bg-[#E8F9F6] px-3 py-1 rounded-full">
                              <StatusIcon color="#17C3A5" />
                              <span className="text-[10px] font-bold text-[#17C3A5]">
                                {status.toUpperCase()}
                              </span>
                            </div>
                          ) : (
                            <div className="flex items-center gap-2 bg-[#FFEFD5] px-3 py-1 rounded-full">
                              <StatusIcon color="#FFC107" />
                              <span className="text-[10px] font-bold text-[#FFC107]">
                                {status.toUpperCase()}
                              </span>
                            </div>
                          )}
                        </div>
                      )}
                    </div>

                    <div className="col-span-1 justify-end hidden md:flex">
                      <Button
                        onClick={() => handleSingleTransaction(transaction)}
                        variant="outline"
                        size="icon"
                        className="rounded-full flex-shrink-0 border-gray-200 hover:bg-gray-200 h-10 w-10"
                      >
                        <ArrowRight className="w-4 h-4 text-[#6D6584]" />
                      </Button>
                    </div>
                  </div>
                );
              })
            ) : (
              <p className="flex items-center justify-center py-8 text-[#6D6584]">
                No transactions found.
              </p>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default TransactionHistoryAll;
